/**
 * @fileoverview `DesignSystemLayout` — layout dédié à la route `/design-system`.
 *
 * Remplace la sidebar principale de la console par une sidebar contextuelle
 * qui liste les sections du design system (Colors, Typography, Buttons, …)
 * comme ancres cliquables. Le lien "Design system" n'apparaît PAS dans la
 * sidebar principale (`ConsoleLayout`) : la page reste accessible par URL
 * directe (`/design-system`) — c'est un outil de dev/design, pas une page
 * opérationnelle du backoffice.
 *
 * Convention : la source unique de la liste des sections est ici. La page
 * `DesignSystemPage` importe la même constante `DS_SECTIONS` pour rendre
 * les blocs correspondants — ordre garanti cohérent entre la sidebar et
 * la page.
 */

import { NavLink, Outlet } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { DESIGN_SYSTEM_VERSION, cn } from '@gnpc/ui';
import { DS_SECTIONS } from '../features/design-system/sections/sections-registry.ts';

export function DesignSystemLayout(): JSX.Element {
  return (
    <div className="bg-muted grid min-h-dvh grid-cols-[240px_1fr]">
      {/* Sticky : la sidebar reste visible pendant le scroll de la page.
       * `h-dvh` fixe sa hauteur au viewport, `overflow-y-auto` autorise
       * un scroll interne si la liste des sections dépasse. */}
      <aside className="bg-quipux-navy-950 sticky top-0 flex h-dvh flex-col gap-1 overflow-y-auto px-4 py-6 text-white">
        <div className="mb-6 space-y-3 px-3">
          <NavLink
            to="/"
            className="text-quipux-navy-200 inline-flex items-center gap-1.5 text-xs transition-colors hover:text-white"
          >
            <ArrowLeft className="size-3.5" />
            Retour à la console
          </NavLink>
          <div>
            <p className="text-lg font-semibold tracking-tight">Design system</p>
            <p className="text-quipux-navy-200 text-xs">
              GNPC · Vitrine live · v{DESIGN_SYSTEM_VERSION.version}
            </p>
          </div>
        </div>

        <nav aria-label="Sections du design system" className="flex flex-col gap-0.5">
          {DS_SECTIONS.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className={cn(
                'group flex items-center justify-between rounded-md px-3 py-1.5 text-sm transition-colors',
                'text-quipux-navy-100 hover:bg-quipux-navy-800 hover:text-white',
              )}
            >
              <span>{section.title}</span>
              {section.component === undefined && (
                <span className="text-quipux-navy-300 group-hover:text-quipux-navy-100 text-[10px] font-medium uppercase">
                  todo
                </span>
              )}
            </a>
          ))}
        </nav>
      </aside>
      <main className="min-w-0 overflow-x-auto p-8">
        <Outlet />
      </main>
    </div>
  );
}
